import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useRecoilRefresher_UNSTABLE } from 'recoil';
import styled, { css } from 'styled-components';
import { useDrop } from 'react-dnd';

import pullAt from 'lodash-es/pullAt';

import { clearSelectMenu } from '@ecp/common/src/utils/utils';
import boTheme from '@ecp/common/src/style/theme/bo';
import { zIndex } from '@ecp/common/src/style/constant';
import { FlexBox, FlexBoxCenter, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import HighlightText from '@ecp/common/src/text/HighlightText';
import Button from '@ecp/common/src/components/button/bo/Button';

import { withBookmarkMenus, withMenuGroups, withMenus } from '@bo-recoil/common/selector';
import { useBookmarkMenu } from '@bo-hooks/common/useBookmarkMenu';
import Autocomplete from '@bo-components/common/autocomplete/Autocomplete';
import PoPrivacyPolicyPopup from '@bo-components/po/system/popups/PrivacyPolicyPopup';
import CoPrivacyPolicyPopup from '@bo-components/co/system/popups/PrivacyPolicyPopup';
import LnbBookmarkMenu, { DND_ITEM_TYPE } from './LnbBookmarkMenu';
import { isCODomain, isPODomain } from '@bo-const/common/domain';
import { ReactComponent as CI } from '@bo-assets/image/common/image__ci.svg';
import { ReactComponent as FoldingIcon } from '@bo-assets/icon/common/icon__lnb-folding.svg';
import LnbMenuGroup from '@bo-layout/lnb/LnbMenuGroup';

const Lnb = () => {
  const [open, setOpen] = useState(true);
  const menuGroupRefList = useRef([]);
  const [searchMenuKeyword, setSearchMenuKeyword] = useState('');
  const menuGroups = useRecoilValue(withMenuGroups);
  const menus = useRecoilValue(withMenus);
  const refreshMenuGroups = useRecoilRefresher_UNSTABLE(withMenuGroups);
  const bookmarkMenus = useRecoilValue(withBookmarkMenus);

  const [clonedBookmarkMenus, setClonedBookmarkMenus] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [isMyMenu, setIsMyMenu] = useState(false);
  const [privacyPolicyPopupOpen, setPrivacyPolicyPopupOpen] = useState(false);

  useEffect(() => {
    setClonedBookmarkMenus([...bookmarkMenus]);
  }, [bookmarkMenus]);
  useEffect(
    () => () => {
      clearSelectMenu();
    },
    []
  );

  useEffect(() => {
    // 컴포넌트가 마운트/로드 될 때마다 캐시 무효화 및 데이터 재요청
    refreshMenuGroups();
  }, [refreshMenuGroups]);

  useEffect(() => {
    menuGroupRefList.current = menuGroupRefList.current.slice(0, menuGroups.length);
  }, [menuGroups]);

  const [, drop] = useDrop(() => ({ accept: DND_ITEM_TYPE }));
  const { updateBookmarkMenus } = useBookmarkMenu();

  const navigate = useNavigate();
  const leafMenus = useMemo(
    () =>
      Object.values(menus)
        .filter(({ menuUrl }) => menuUrl)
        .map((menu) => {
          return { itemLabel: menu.menuName, itemUrl: menu.menuUrl, ...menu };
        }),
    [menus]
  );

  const handleOpenClick = () => {
    setOpen((prev) => !prev);
  };

  const handleBookmarkMenuMove = (dragMenuNumber, overMenuNumber) => {
    const newBookmarkMenus = [...clonedBookmarkMenus];
    const dragStartIndex = newBookmarkMenus.indexOf(dragMenuNumber);
    const dragEndIndex = newBookmarkMenus.indexOf(overMenuNumber);

    const [pulled] = pullAt(newBookmarkMenus, dragStartIndex);
    newBookmarkMenus.splice(dragStartIndex < dragEndIndex ? dragEndIndex + 1 : dragEndIndex, 0, pulled);

    setClonedBookmarkMenus(newBookmarkMenus);
  };

  const handleBookmarkMenuDrop = async () => {
    await updateBookmarkMenus(clonedBookmarkMenus);
  };

  const handleSearchMenuKeywordChange = (event) => {
    const searchKeyword = event?.target?.value;

    setSearchMenuKeyword(searchKeyword);

    if (!(searchKeyword?.length > 0)) {
      setShowSearchResult(false);
    }
  };

  const handleMenuItemClick = (name, { itemUrl: url }) => {
    setShowSearchResult(false);
    setSearchMenuKeyword('');
    if (url) navigate(url);
  };

  const handleSearchClick = (result) => {
    if (searchMenuKeyword === '') {
      return;
    }

    setSearchResult(result);
    setShowSearchResult(true);
  };

  const handleTabButtonClick = useCallback(
    (selectMyMenu) => {
      setIsMyMenu(selectMyMenu);
    },
    [setIsMyMenu]
  );

  const handlePrivacyPolicyButtonClick = useCallback(
    () => setPrivacyPolicyPopupOpen(!privacyPolicyPopupOpen),
    [privacyPolicyPopupOpen]
  );

  const handlePrivacyPolicyPopupConfirmButtonClick = useCallback(() => setPrivacyPolicyPopupOpen(false), []);
  const goToHome = () => {
    menuGroupRefList.current.forEach((menuGroup) => menuGroup?.close?.());
    navigate('/');
  };

  return (
    <Container open={open}>
      <LogoWrapper onClick={goToHome}>
        <Spacing left={21} />
        <CI width={100} height={16} />
      </LogoWrapper>
      <SearchWrapper>
        <Autocomplete
          placeholder={'메뉴명 검색'}
          width={'240px'}
          onChange={handleSearchMenuKeywordChange}
          value={searchMenuKeyword}
          setValue={setSearchMenuKeyword}
          items={leafMenus}
          handleItemClick={handleMenuItemClick}
          handleSearchClick={handleSearchClick}
        />
      </SearchWrapper>
      {showSearchResult ? (
        <SearchResultContainer>
          <SearchResultTitle
            size={boTheme.font.size.medium}
            color={boTheme.color.menu5}
            weight={boTheme.font.weight.bold}
          >
            검색결과
          </SearchResultTitle>
          <SearchResultBody>
            {searchResult?.length > 0 ? (
              <>
                {searchResult.map(({ itemLabel, itemUrl }, index) => (
                  <SearchResultItem key={index} onClick={() => handleMenuItemClick(itemLabel, { itemUrl })}>
                    <HighlightText keyword={searchMenuKeyword} style={{ fontWeight: '700' }}>
                      {itemLabel}
                    </HighlightText>
                  </SearchResultItem>
                ))}
              </>
            ) : (
              <SearchResultEmpty>검색결과가 없습니다.</SearchResultEmpty>
            )}
          </SearchResultBody>
        </SearchResultContainer>
      ) : (
        <MenusWrapper>
          <FlexBoxColumn flex-grow={'1'}>
            <FlexBox width={'100%'}>
              <TabButton selected={!isMyMenu} onClick={() => handleTabButtonClick(false)}>
                전체메뉴
              </TabButton>
              <TabButton selected={isMyMenu} onClick={() => handleTabButtonClick(true)}>
                나의메뉴
              </TabButton>
            </FlexBox>
            <FlexBoxColumn width="100%" style={{ backgroundColor: `${boTheme.color.menu1}` }}>
              <Spacing top={10} />
              <MenusTabPanelWrapper isPo={process.env.REACT_APP_ENV.includes('po')} ref={isMyMenu ? drop : null}>
                {!isMyMenu
                  ? menuGroups?.map((menuGroupNumber, idx) => (
                      <LnbMenuGroup
                        key={menuGroupNumber}
                        menuGroupNumber={menuGroupNumber}
                        ref={(el) => (menuGroupRefList.current[idx] = el)}
                      />
                    ))
                  : clonedBookmarkMenus?.map((bookmarkMenuNumber) => (
                      <LnbBookmarkMenu
                        key={bookmarkMenuNumber}
                        bookmarkMenuNumber={bookmarkMenuNumber}
                        onMove={handleBookmarkMenuMove}
                        onDrop={handleBookmarkMenuDrop}
                      />
                    ))}
              </MenusTabPanelWrapper>
              {process.env.REACT_APP_ENV.includes('po') && !isMyMenu && (
                <>
                  <Spacing top={20} />
                  <Button type={'Third'} width={'230px'} height={'30px'} onClick={handlePrivacyPolicyButtonClick}>
                    개인정보처리방침
                  </Button>
                </>
              )}
            </FlexBoxColumn>
          </FlexBoxColumn>
        </MenusWrapper>
      )}
      <OpenButton className="folding-button" onClick={handleOpenClick} open={open}>
        <FoldingIcon width={28} height={28} />
      </OpenButton>
      <BottomMargin top={20} />
      { isPODomain() && <PoPrivacyPolicyPopup open={privacyPolicyPopupOpen} onConfirmClick={handlePrivacyPolicyPopupConfirmButtonClick} /> }
      { isCODomain() && <CoPrivacyPolicyPopup open={privacyPolicyPopupOpen} onConfirmClick={handlePrivacyPolicyPopupConfirmButtonClick} /> }
    </Container>
  );
};

const closeStyle = css`
  & div:not(.folding-button) {
    visibility: hidden;
  }

  & .folding-button {
    transform: rotate(180deg);
    right: 10px;
  }
`;

const Container = styled(FlexBoxColumn)`
  width: ${({ open }) => (open ? '280px' : '48px')};
  height: 100vh;
  flex-grow: 0;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;

  background-color: ${boTheme.color.menu4};

  ${({ open }) => (open ? '' : closeStyle)};
  z-index: ${zIndex.modal};
`;

const LogoWrapper = styled(FlexBox)`
  width: 100%;
  height: 50px;
  flex-grow: 0;
  flex-shrink: 0;
  cursor: pointer;
`;

const SearchWrapper = styled(FlexBoxCenter)`
  width: 100%;
  height: 68px;
  flex-grow: 0;
  flex-shrink: 0;

  position: relative;
`;

const SearchResultContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${boTheme.color.menu1};
  padding: 20px 0 0 24px;
`;

const SearchResultTitle = styled(TextBox)`
  width: 232px;
  height: 30px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${boTheme.color.menu6};
`;

const SearchResultBody = styled(FlexBoxColumn)`
  width: 256px;
  height: calc(100vh - 190px);
  align-items: flex-start;
  overflow-y: auto;
  padding-top: 20px;
  gap: 20px;
`;

const SearchResultItem = styled(FlexBox)`
  font-weight: ${boTheme.font.weight.regular};
  font-size: ${boTheme.font.size.small};
  color: ${boTheme.color.sub10};
  cursor: pointer;
`;

const SearchResultEmpty = styled(FlexBox)`
  font-weight: ${boTheme.font.weight.regular};
  font-size: ${boTheme.font.size.small};
  color: ${boTheme.color.sub10};
`;

const MenusWrapper = styled(FlexBoxCenter)`
  width: 100%;
  align-items: stretch;
  overflow: hidden;
  background-color: ${boTheme.color.menu1};
`;

const OpenButton = styled.div`
  width: 28px;
  height: 28px;
  position: absolute;
  top: 11px;
  right: 20px;
  cursor: pointer;
`;

const TabButton = styled(FlexBoxCenter)`
  height: 42px;
  flex-grow: 1;
  font-size: ${boTheme.font.size.medium};
  font-weight: ${boTheme.font.weight.bold};
  color: ${boTheme.color.sub10};
  background-color: ${boTheme.color.menu4};
  cursor: pointer;

  ${({ selected }) => selected && `background-color: ${boTheme.color.menu1}`};
`;

const MenusTabPanelWrapper = styled(FlexBoxColumn)`
  width: 100%;
  height: calc(100vh - ${({ isPo }) => (isPo ? '240px' : '190px')});
  align-items: flex-start;
  overflow-y: auto;
`;

const BottomMargin = styled(Spacing)`
  width: 100%;
  background-color: ${boTheme.color.menu1};
`;

export default Lnb;
